import { del, get, keys, set } from 'idb-keyval';

/**
 * Незаконченная запись переживает перезагрузку страницы (§5.3 сверки, 12.09.2026).
 *
 * 🔴 ЗАЧЕМ. Ученик говорит две минуты, вкладка перезагружается — на телефоне это
 * делает и сама система, когда ей не хватает памяти, — и работа пропадает
 * целиком. Куски ложатся сюда по мере того, как их отдаёт рекордер.
 *
 * ⚠️ ЭТО ПОДСТРАХОВКА, А НЕ ОБЕЩАНИЕ. Safari на iPhone чистит хранилище сайта
 * через неделю без визита, квота может кончиться, приватное окно не хранит
 * ничего. Ученику об этом сказано прямо, рядом с предложением восстановить.
 *
 * ⚠️ ХРАНИМ БАЙТЫ, А НЕ `Blob`. Тот же урок, что у очереди отправки: объект файла
 * переживает сохранение не на всяком устройстве, и запись молча превратилась бы
 * в ноль байт.
 *
 * ⚠️ КУСКИ — ОТДЕЛЬНЫМИ КЛЮЧАМИ. Держи мы их одним массивом, каждый новый кусок
 * переписывал бы всё накопленное: на трёх минутах это десятки мегабайт записи в
 * хранилище впустую.
 *
 * ⚠️ ОЧЕРЕДЬ ОТВЕТОВ ЖИВЁТ В ТОМ ЖЕ ХРАНИЛИЩЕ. Переполнить квоту ради черновика
 * значит уронить отправку чужих ответов — это дороже одной записи. Отсюда
 * жёсткий потолок и отказ от черновика, когда он превышен.
 */

/**
 * Как часто рекордер отдаёт кусок.
 *
 * Пять секунд — размен: чаще значит больше записей в хранилище впустую, реже —
 * больше потеряется при внезапной перезагрузке. По секунде писать не стоит: на
 * трёх минутах это 180 записей.
 */
export const DRAFT_CHUNK_MS = 5000;

const PREFIX = 'onebilim:recdraft:';
const metaKey = (blockId: string) => `${PREFIX}${blockId}`;
const partKey = (blockId: string, index: number) => `${PREFIX}${blockId}:${index}`;

/** Больше этого не копим: дальше рискуем квотой, в которой живёт очередь ответов. */
export const MAX_DRAFT_BYTES = 12 * 1024 * 1024;

/** Дольше суток черновик не предлагаем: ученик уже забыл, о чём говорил. */
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

type DraftMeta = {
  mime: string;
  count: number;
  bytes: number;
  elapsedMs: number;
  updatedAt: number;
};

export type RecordingDraft = {
  blockId: string;
  mime: string;
  parts: ArrayBuffer[];
  elapsedMs: number;
  updatedAt: number;
};

/** Любая работа с хранилищем необязательна: отказ не должен ронять запись. */
async function quiet<T>(work: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await work();
  } catch {
    return fallback;
  }
}

async function readMeta(blockId: string): Promise<DraftMeta | null> {
  const meta = await quiet(() => get<DraftMeta>(metaKey(blockId)), undefined);
  return meta && typeof meta.count === 'number' ? meta : null;
}

/** Стереть черновик задания целиком, вместе с кусками. */
export async function dropDraft(blockId: string): Promise<void> {
  const meta = await readMeta(blockId);
  const count = meta?.count ?? 0;
  await quiet(async () => {
    for (let i = 0; i < count; i++) await del(partKey(blockId, i));
    await del(metaKey(blockId));
  }, undefined);
}

/**
 * Начать новый черновик.
 *
 * ⚠️ ПРЕЖНИЙ СТИРАЕТСЯ. Иначе к новым кускам приклеились бы старые, и вышел бы
 * файл с двумя заголовками — читается такой только до первого куска.
 */
export async function openDraft(blockId: string, mime: string, elapsedMs: number): Promise<boolean> {
  await dropDraft(blockId);
  return quiet(async () => {
    await set(metaKey(blockId), { mime, count: 0, bytes: 0, elapsedMs, updatedAt: Date.now() } satisfies DraftMeta);
    return true;
  }, false);
}

/** Дописать кусок. `false` — черновика больше не будет (отказ хранилища или потолок). */
export async function appendChunk(blockId: string, chunk: ArrayBuffer, elapsedMs: number): Promise<boolean> {
  if (!chunk || !chunk.byteLength) return false;
  const meta = await readMeta(blockId);
  if (!meta) return false;

  if (meta.bytes + chunk.byteLength > MAX_DRAFT_BYTES) {
    // Обрубок предлагать нечестно: ученик согласится «восстановить», а получит
    // половину. Убираем целиком и дальше не копим.
    await dropDraft(blockId);
    return false;
  }

  return quiet(async () => {
    await set(partKey(blockId, meta.count), chunk);
    await set(metaKey(blockId), {
      ...meta,
      count: meta.count + 1,
      bytes: meta.bytes + chunk.byteLength,
      elapsedMs,
      updatedAt: Date.now(),
    } satisfies DraftMeta);
    return true;
  }, false);
}

/** Черновик задания, если он есть и не просрочен. */
export async function readDraft(blockId: string, now = Date.now()): Promise<RecordingDraft | null> {
  const meta = await readMeta(blockId);
  if (!meta || !meta.count) return null;
  if (now - meta.updatedAt > DRAFT_TTL_MS) return null;

  const parts: ArrayBuffer[] = [];
  for (let i = 0; i < meta.count; i++) {
    const part = await quiet(() => get<ArrayBuffer>(partKey(blockId, i)), undefined);
    // Кусок потерялся — дальше собирать нечего: дыра посреди файла делает его
    // нечитаемым, и честнее не предлагать ничего.
    if (!part || !part.byteLength) return null;
    parts.push(part);
  }
  return { blockId, mime: meta.mime, parts, elapsedMs: meta.elapsedMs, updatedAt: meta.updatedAt };
}

/** Выбросить просроченные черновики. Зовётся на старте приложения. */
export async function pruneDrafts(now = Date.now()): Promise<void> {
  const all = await quiet(() => keys(), [] as IDBValidKey[]);
  const ids = new Set<string>();
  for (const key of all) {
    if (typeof key !== 'string' || !key.startsWith(PREFIX)) continue;
    const rest = key.slice(PREFIX.length);
    // Ключ куска — «задание:номер»; нас интересует само задание.
    ids.add(rest.split(':')[0]);
  }
  for (const id of ids) {
    const meta = await readMeta(id);
    if (!meta || now - meta.updatedAt > DRAFT_TTL_MS) await dropDraft(id);
  }
}
